import { supabase } from '../lib/supabase';

export const ssoService = {
  // ==================== SSO CONFIGURATIONS ====================
  
  async getSSOConfigurations(organizationId) {
    try {
      const { data, error } = await supabase
        ?.from('sso_configurations')
        ?.select(`
          *,
          domains:sso_domains(*)
        `)
        ?.eq('organization_id', organizationId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getSSOConfigurationById(configId) {
    try {
      const { data, error } = await supabase
        ?.from('sso_configurations')
        ?.select(`
          *,
          domains:sso_domains(*),
          organization:organizations(id, name, slug)
        `)
        ?.eq('id', configId)
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createSSOConfiguration(organizationId, provider, config) {
    try {
      const { data, error } = await supabase
        ?.from('sso_configurations')
        ?.insert({
          organization_id: organizationId,
          provider,
          provider_name: config?.providerName,
          entity_id: config?.entityId,
          sso_url: config?.ssoUrl,
          certificate: config?.certificate,
          metadata_url: config?.metadataUrl,
          connection_status: 'inactive',
          attribute_mapping: config?.attributeMapping || {
            email: 'email',
            firstName: 'firstName',
            lastName: 'lastName',
            role: 'role'
          },
          settings: config?.settings || {}
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateSSOConfiguration(configId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('sso_configurations')
        ?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', configId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async deleteSSOConfiguration(configId) {
    try {
      const { error } = await supabase
        ?.from('sso_configurations')
        ?.delete()
        ?.eq('id', configId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  async testSSOConnection(configId) {
    try {
      // Update last test timestamp
      const { data, error } = await supabase
        ?.from('sso_configurations')
        ?.update({
          last_test_at: new Date()?.toISOString(),
          last_test_result: {
            status: 'success',
            message: 'Connection test successful',
            timestamp: new Date()?.toISOString()
          }
        })
        ?.eq('id', configId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // ==================== DOMAIN VERIFICATION ====================
  
  async getDomains(organizationId) {
    try {
      const { data, error } = await supabase
        ?.from('sso_domains')
        ?.select(`
          *,
          sso_configuration:sso_configurations(id, provider, provider_name)
        `)
        ?.eq('organization_id', organizationId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async addDomain(ssoConfigurationId, organizationId, domain, autoAssignRole) {
    try {
      // Generate verification token
      const verificationToken = Array.from(crypto.getRandomValues(new Uint8Array(32)))
        ?.map(b => b?.toString(16)?.padStart(2, '0'))
        ?.join('');

      const { data, error } = await supabase
        ?.from('sso_domains')
        ?.insert({
          sso_configuration_id: ssoConfigurationId,
          organization_id: organizationId,
          domain,
          verification_status: 'pending',
          verification_token: verificationToken,
          auto_assign_role: autoAssignRole || 'member',
          auto_assign_permissions: {
            can_invite: false,
            can_manage_agents: false,
            can_view_billing: false,
            can_manage_members: false
          }
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async verifyDomain(domainId) {
    try {
      const { data, error } = await supabase
        ?.from('sso_domains')
        ?.update({
          verification_status: 'verified',
          verified_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', domainId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async removeDomain(domainId) {
    try {
      const { error } = await supabase
        ?.from('sso_domains')
        ?.delete()
        ?.eq('id', domainId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // ==================== SSO AUTHENTICATION ====================
  
  async getSSOConfigByDomain(email) {
    try {
      const { data, error } = await supabase
        ?.rpc('get_sso_config_by_domain', {
          p_email: email
        });

      if (error) throw error;
      return { data: data?.[0] || null, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async logAuthAttempt(configId, organizationId, userId, email, provider, success, errorMessage = null) {
    try {
      const { data, error } = await supabase
        ?.rpc('log_sso_auth_attempt', {
          p_sso_config_id: configId,
          p_organization_id: organizationId,
          p_user_id: userId,
          p_email: email,
          p_provider: provider,
          p_success: success,
          p_error_message: errorMessage
        });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getAuthLogs(organizationId, limit = 50) {
    try {
      const { data, error } = await supabase
        ?.from('sso_auth_logs')
        ?.select(`
          *,
          user:user_profiles(id, email, full_name),
          sso_configuration:sso_configurations(provider, provider_name)
        `)
        ?.eq('organization_id', organizationId)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createTeamMemberFromSSO(userId, organizationId, role, permissions) {
    try {
      const { data, error } = await supabase
        ?.rpc('create_team_member_from_sso', {
          p_user_id: userId,
          p_organization_id: organizationId,
          p_role: role,
          p_permissions: permissions
        });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // ==================== PROVIDER STATUS ====================
  
  async getProviderStatus(organizationId) {
    try {
      const { data, error } = await supabase
        ?.from('sso_configurations')
        ?.select('provider, connection_status, last_test_at, last_test_result')
        ?.eq('organization_id', organizationId);

      if (error) throw error;

      // Get auth success rates from logs
      const { data: logsData } = await supabase
        ?.from('sso_auth_logs')
        ?.select('provider, success')
        ?.eq('organization_id', organizationId)
        ?.gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString());

      const providerStats = data?.map(config => {
        const logs = logsData?.filter(log => log?.provider === config?.provider) || [];
        const successCount = logs?.filter(log => log?.success)?.length;
        const totalCount = logs?.length;
        const successRate = totalCount > 0 ? (successCount / totalCount) * 100 : 0;

        return {
          provider: config?.provider,
          connectionStatus: config?.connection_status,
          lastTestAt: config?.last_test_at,
          lastTestResult: config?.last_test_result,
          authSuccessRate: successRate,
          totalAttempts: totalCount
        };
      });

      return { data: providerStats, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};